import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  EventSchema,
  EventCreateInputSchema,
  EventUpdateInputSchema,
  type Event,
  type EventCreateInput,
  type EventUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `event` (occurrence datée à capacité partagée,
 * org-wide).
 *
 * Même patron que `asset` : l'entrée est VALIDÉE par Zod
 * (`EventCreateInputSchema`) AVANT toute écriture Firestore. On n'écrit jamais
 * un document pour découvrir ensuite qu'il est invalide. Les inscriptions et les
 * présences vivent dans leurs propres familles (`registration`, `attendance`)
 * qui pointent l'event ; on ne les stocke pas ici.
 */

export type { EventCreateInput };

/** Nom de la collection Firestore de la famille event. */
export const EVENTS_COLLECTION = 'events';

/**
 * Repository typé pour la famille `event`.
 * Toute lecture est validée par `EventSchema`, toute écriture par
 * `EventCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface EventRepository {
  create(input: EventCreateInput): Promise<Event>;
  list(options?: ListOptions): Promise<Event[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Event | null>;
  update(id: string, patch: EventUpdateInput): Promise<Event>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Event>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Event>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Event>;
}

const eventConverter = createConverter<Event>((raw: DocumentData) => EventSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createEventRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): EventRepository {
  const col = collection(db, EVENTS_COLLECTION).withConverter(eventConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Event>(db, col, EVENTS_COLLECTION, getActorId);

  return {
    async create(input: EventCreateInput): Promise<Event> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore.
      const fields = EventCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.startAt !== undefined) optional['startAt'] = fields.startAt;
      if (fields.endAt !== undefined) optional['endAt'] = fields.endAt;
      if (fields.capacity !== undefined) optional['capacity'] = fields.capacity;
      if (fields.price !== undefined) optional['price'] = fields.price;
      if (fields.recurrenceRule !== undefined) optional['recurrenceRule'] = fields.recurrenceRule;
      if (fields.registrationDeadline !== undefined) optional['registrationDeadline'] = fields.registrationDeadline;
      if (fields.resourceId !== undefined) optional['resourceId'] = fields.resourceId;
      if (fields.placeId !== undefined) optional['placeId'] = fields.placeId;
      if (fields.workerIds !== undefined) optional['workerIds'] = fields.workerIds;
      if (fields.campaignId !== undefined) optional['campaignId'] = fields.campaignId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Event);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return EventSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async list(options?: ListOptions): Promise<Event[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Event | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    async update(id: string, patch: EventUpdateInput): Promise<Event> {
      // Valider AVANT d'ecrire (meme doctrine que create).
      const fields = EventUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'ecriture partielle : le converter
      // exigerait le document complet.
      await updateDoc(doc(db, EVENTS_COLLECTION, id), data);

      // Relire le document reel via la ref convertie : source de verite.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`event ${id} introuvable apres mise a jour.`);
      }
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
